import styled from "styled-components";
import Button from "../Button/Button";

export const OuterContainer = styled.div`
  --contacts-action-width: min(40%, 760px);
  --contacts-action-height: 115px;
  --contacts-action-gap: ${({ theme }) => theme.padding.regular};
  --contacts-cutout-inner-radius: ${({ theme }) => theme.borderRadius.medium};
  --contacts-cutout-edge-radius: ${({ theme }) => theme.borderRadius.xlarge};

  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  gap: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.padding.xlarge}
    ${({ theme }) => theme.padding.medium};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge}
    ${({ theme }) => theme.borderRadius.xlarge} 0 0;
  background-color: ${({ theme }) => theme.colors.grey};
  color: ${({ theme }) => theme.colors.darkBlue};

  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    width: calc(
      var(--contacts-action-width) + var(--contacts-action-gap) +
        var(--contacts-action-gap)
    );
    height: calc(var(--contacts-action-height) + var(--contacts-action-gap));
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: var(--contacts-cutout-inner-radius);
    background-color: ${({ theme }) => theme.colors.background};
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    top: calc(var(--contacts-action-height) + var(--contacts-action-gap));
    right: 0;
    z-index: 1;
    width: var(--contacts-cutout-edge-radius);
    height: var(--contacts-cutout-edge-radius);
    background-color: ${({ theme }) => theme.colors.background};
    mask-image: radial-gradient(
      circle at 0 100%,
      transparent 0 calc(var(--contacts-cutout-edge-radius) - 0.5px),
      #000 var(--contacts-cutout-edge-radius)
    );
    mask-repeat: no-repeat;
    -webkit-mask-image: radial-gradient(
      circle at 0 100%,
      transparent 0 calc(var(--contacts-cutout-edge-radius) - 0.5px),
      #000 var(--contacts-cutout-edge-radius)
    );
    -webkit-mask-repeat: no-repeat;
    pointer-events: none;
  }

  @media (max-width: 1024px) {
    gap: ${({ theme }) => theme.spacing.medium};
    padding: ${({ theme }) => theme.padding.large}
      ${({ theme }) => theme.padding.regular};
    border-radius: ${({ theme }) => theme.borderRadius.large}
      ${({ theme }) => theme.borderRadius.large} 0 0;

    &::before,
    &::after {
      display: none;
    }
  }

  @media (max-width: 520px) {
    gap: ${({ theme }) => theme.spacing.regular};
    padding: ${({ theme }) => theme.padding.regular}
      ${({ theme }) => theme.padding.small};
    border-radius: ${({ theme }) => theme.borderRadius.medium}
      ${({ theme }) => theme.borderRadius.medium} 0 0;
  }

  @media (min-width: 2500px) {
    padding: ${({ theme }) => theme.padding.xlarge}
      ${({ theme }) => theme.padding.large};
  }
  @media (min-width: 3000px) {
    --contacts-action-width: min(40%, 900px);
    --contacts-action-height: 140px;
    --contacts-action-gap: ${({ theme }) => theme.padding.medium};
    --contacts-cutout-inner-radius: ${({ theme }) => theme.borderRadius.large};
  }
  @media (min-width: 3400px) {
    --contacts-action-width: min(40%, 980px);
    --contacts-action-height: 160px;
  }
  @media (min-width: 3500px) {
    padding: ${({ theme }) => theme.padding.xlarge} 1.5dvw;
  }
  @media (min-width: 3800px) {
    --contacts-action-width: min(40%, 1100px);
    --contacts-action-height: 200px;
    --contacts-cutout-inner-radius: ${({ theme }) => theme.borderRadius.xlarge};
    padding: ${({ theme }) => theme.spacing.spacingTablet} 1.5dvw;
    gap: ${({ theme }) => theme.spacing.spacingTablet};
  }
  @media (min-width: 4500px) {
    --contacts-action-width: min(40%, 1300px);
    --contacts-action-height: 260px;
  }
  @media (min-width: 5000px) {
    --contacts-action-width: min(40%, 1400px);
    --contacts-action-height: 280px;
  }
`;

export const ContactsHeader = styled.div`
  h1 {
    @media (max-width: 520px) {
      font-size: ${({ theme }) => theme.typography.h2.fontSize};
    }
    @media (min-width: 2500px) {
      font-size: 4rem;
    }
    @media (min-width: 3400px) {
      font-size: 5.25rem;
      word-spacing: ${({ theme }) => theme.spacing.small};
    }
    @media (min-width: 3800px) {
      font-size: 5.75rem;
    }
    @media (min-width: 4500px) {
      font-size: 6.25rem;
    }
    @media (min-width: 5000px) {
      font-size: 6.75rem;
    }
  }
`;

export const ContactsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: ${({ theme }) => theme.spacing.medium};
  row-gap: ${({ theme }) => theme.spacing.large};
  width: 100%;

  > :nth-child(1) {
    grid-column: 1;
    grid-row: 1;
  }

  > :nth-child(2) {
    grid-column: 2;
    grid-row: 1;
  }

  > :nth-child(3) {
    grid-column: 2;
    grid-row: 2;
  }

  > :nth-child(4) {
    grid-column: 3;
    grid-row: 2;
  }

  > :nth-child(5) {
    grid-column: 4;
    grid-row: 2;
  }

  @media (max-width: 1280px) {
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.2fr);
    column-gap: ${({ theme }) => theme.spacing.medium};
    row-gap: 0;

    > :nth-child(1) {
      grid-column: 1;
      grid-row: 1 / span 4;
    }

    > :nth-child(n + 2) {
      grid-column: 2;
      grid-row: auto;
    }
  }

  @media (max-width: 520px) {
    display: flex;
    flex-direction: column;
  }

  @media (min-width: 3800px) {
    row-gap: ${({ theme }) => theme.spacing.spacingTablet};
    column-gap: 52px;
  }
`;

export const Contact = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.regular};
  min-width: 0;

  @media (max-width: 1280px) {
    padding: 0 0 ${({ theme }) => theme.spacing.regular};

    &:nth-child(n + 2):not(:last-child) {
      margin-bottom: ${({ theme }) => theme.spacing.regular};
      border-bottom: 1px solid ${({ theme }) => theme.colors.darkBlue};
    }
  }

  @media (max-width: 520px) {
    gap: ${({ theme }) => theme.spacing.small};

    &:not(:last-child) {
      margin-bottom: ${({ theme }) => theme.spacing.regular};
      border-bottom: 1px solid ${({ theme }) => theme.colors.darkBlue};
    }

    &:last-child {
      padding-bottom: 0;
    }
  }

  @media (min-width: 2500px) {
    gap: ${({ theme }) => theme.padding.medium};
  }
  @media (min-width: 3400px) {
    gap: ${({ theme }) => theme.padding.large};
  }
  @media (min-width: 3800px) {
    gap: 52px;
  }
  @media (min-width: 4500px) {
    gap: 64px;
  }
`;

export const ContactName = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};

  h3 {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.body.fontSize};
    font-weight: ${({ theme }) => theme.fontWeight.extrabold};
    text-transform: uppercase;
  }

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.body.fontSize};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    line-height: 1.35;
    white-space: pre-line;
  }

  @media (max-width: 520px) {
    h3,
    p {
      font-size: ${({ theme }) => theme.typography.medium.fontSize};
    }
  }
  @media (min-width: 2500px) {
    h3,
    p {
      font-size: 1.75rem;
    }
  }
  @media (min-width: 3000px) {
    h3,
    p {
      font-size: 2rem;
    }
  }
  @media (min-width: 3400px) {
    h3,
    p {
      font-size: 2.5rem;
    }
  }
  @media (min-width: 3800px) {
    h3,
    p {
      font-size: 3rem;
    }
  }
  @media (min-width: 4500px) {
    h3,
    p {
      font-size: 3.25rem;
    }
  }
  @media (min-width: 5000px) {
    h3,
    p {
      font-size: 3.75rem;
    }
  }
`;

export const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};

  @media (max-width: 520px) {
    gap: 2px;
  }

  @media (min-width: 3000px) {
    gap: ${({ theme }) => theme.spacing.small};
  }
  @media (min-width: 3400px) {
    gap: 12px;
  }
`;

export const ContactLink = styled.a`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  color: inherit;
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: 1.3;
  text-decoration: none;
  transition: color 0.25s ease;

  svg {
    flex: 0 0 auto;
  }

  span {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.colors.accent};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 3px;
    border-radius: ${({ theme }) => theme.borderRadius.xsmall};
  }

  @media (max-width: 520px) {
    gap: ${({ theme }) => theme.spacing.xsmall};
    font-size: ${({ theme }) => theme.typography.medium.fontSize};
  }

  @media (min-width: 2500px) {
    font-size: 1.75rem;

    svg {
      width: 32px;
      height: 32px;
    }
  }
  @media (min-width: 3000px) {
    font-size: 2rem;
    gap: ${({ theme }) => theme.spacing.regular};
  }
  @media (min-width: 3400px) {
    font-size: 2.5rem;
    gap: ${({ theme }) => theme.spacing.medium};

    svg {
      width: 42px;
      height: 42px;
    }
  }
  @media (min-width: 3800px) {
    font-size: 3rem;

    svg {
      width: 52px;
      height: 52px;
    }
  }
  @media (min-width: 4500px) {
    font-size: 3.25rem;

    svg {
      width: 56px;
      height: 56px;
    }
  }
  @media (min-width: 5000px) {
    font-size: 3.75rem;

    svg {
      width: 64px;
      height: 64px;
    }
  }
`;

export const ActionContainer = styled.div`
  position: absolute;
  top: 0;
  right: var(--contacts-action-gap);
  z-index: 2;
  display: flex;
  width: var(--contacts-action-width);
  height: var(--contacts-action-height);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: calc(
      0px - var(--contacts-action-gap) - var(--contacts-cutout-edge-radius)
    );
    z-index: 0;
    width: var(--contacts-cutout-edge-radius);
    height: var(--contacts-cutout-edge-radius);
    background-color: ${({ theme }) => theme.colors.background};
    mask-image: radial-gradient(
      circle at 0 100%,
      transparent 0 calc(var(--contacts-cutout-edge-radius) - 0.5px),
      #000 var(--contacts-cutout-edge-radius)
    );
    mask-repeat: no-repeat;
    -webkit-mask-image: radial-gradient(
      circle at 0 100%,
      transparent 0 calc(var(--contacts-cutout-edge-radius) - 0.5px),
      #000 var(--contacts-cutout-edge-radius)
    );
    -webkit-mask-repeat: no-repeat;
    pointer-events: none;
  }

  @media (max-width: 1024px) {
    position: static;
    z-index: auto;
    width: auto;
    height: auto;
    transform: none;
    align-self: flex-end;

    &::before {
      display: none;
    }
  }

  @media (max-width: 520px) {
    width: 100%;
  }
`;

export const DesktopActionButton = styled(Button)`
  display: flex;
  width: 100%;
  inline-size: 100%;
  height: 100%;
  align-self: stretch;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.h2.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.bold};

  span {
    margin-right: ${({ theme }) => theme.spacing.small};
  }

  @media (max-width: 1024px) {
    display: none;
  }

  @media (min-width: 3000px) {
    border-radius: ${({ theme }) => theme.borderRadius.large};
  }
`;

export const ResponsiveActionButton = styled(Button)`
  display: none;

  @media (max-width: 1024px) {
    display: inline-flex;
    width: max-content;
    inline-size: max-content;
  }

  @media (max-width: 520px) {
    width: 100%;
    inline-size: 100%;
    max-width: none;

    > span:first-child {
      flex: 1;
    }
    font-weight: ${({ theme }) => theme.fontWeight.medium};
  }
`;
